export type OrderTier = "standard" | "priority" | "fast" | null | undefined;

export interface StackableOrder {
  _id: string;
  trackId: string;
  searchQuery: string;
  trackArtists: string[];
  trackImage?: string;
  amount?: number | null;
  totalAmount?: number | null;
  originalAmount?: number | null;
  // Bug C — number of bid boosts the fan has done on this order (0-3).
  // Surfaced in the OfferCard badge styling: increaseCount >= 2 triggers
  // the orange "manually boosted" treatment.
  increaseCount?: number;
  comment?: string;
  tier?: OrderTier;
  isPriority?: boolean;
  currency?: string;
  createdAt?: string;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  clientInstagramVerified?: boolean;
  // Setlist sale — every playlist order shares trackId "playlist", but each
  // is its own sale by its own fan: never stack them.
  isPlaylist?: boolean;
  // ⭐ FAN BOOST — aggregated per stack (anyFanBoost), same pattern as tier.
  fanBoost?: boolean;
}

export interface StackContributor {
  nickname?: string | null;
  instagram?: string | null;
  instagramVerified?: boolean;
  createdAt?: string;
}

export interface OrderStack<T extends StackableOrder> {
  lead: T;
  stackedIds: string[];
  stackCount: number;
  totalStackAmount: number;
  highestTier: "standard" | "priority" | "fast";
  anyFast: boolean;
  // True when ANY request in the stack is fan-boosted — the gold treatment
  // must survive stacking (the boosted fan's row may not be the lead).
  anyFanBoost: boolean;
  comments: string[];
  contributors: StackContributor[];
}

const TIER_RANK: Record<"standard" | "priority" | "fast", number> = {
  standard: 0,
  priority: 1,
  fast: 2,
};

function normalizeTier(t: OrderTier): "standard" | "priority" | "fast" {
  if (t === "fast" || t === "priority") return t;
  return "standard";
}

function stackKey(o: StackableOrder): string {
  // Playlist sales are individual purchases (all share trackId "playlist") —
  // each keeps its own card, its own fan, its own accept.
  if (o.isPlaylist) return `row::${o._id}`;
  const name = (o.searchQuery || "").trim().toLowerCase();
  const artists = (o.trackArtists || [])
    .map((a) => (a || "").trim().toLowerCase())
    .join("|");
  if (name && artists) return `song::${name}::${artists}`;
  if (o.trackId) return `id::${o.trackId}`;
  return `row::${o._id}`;
}

export function groupOrdersByTrack<T extends StackableOrder>(orders: T[]): OrderStack<T>[] {
  const buckets = new Map<string, T[]>();
  for (const o of orders) {
    const key = stackKey(o);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(o);
  }

  const stacks: OrderStack<T>[] = [];
  const bucketsArr = Array.from(buckets.values());
  for (const group of bucketsArr) {
    const sorted = [...group].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });

    let highestTier: "standard" | "priority" | "fast" = "standard";
    let totalStackAmount = 0;
    const stackedIds: string[] = [];
    const comments: string[] = [];
    const contributors: StackContributor[] = [];

    for (const o of sorted) {
      stackedIds.push(o._id);
      totalStackAmount += typeof o.amount === "number" ? o.amount : 0;
      const t = normalizeTier(o.tier);
      if (TIER_RANK[t] > TIER_RANK[highestTier]) highestTier = t;
      if (o.comment) comments.push(o.comment);
      contributors.push({
        nickname: o.clientNickname,
        instagram: o.clientInstagram,
        instagramVerified: o.clientInstagramVerified,
        createdAt: o.createdAt,
      });
    }

    const lead = { ...sorted[0], tier: highestTier } as T;

    stacks.push({
      lead,
      stackedIds,
      stackCount: sorted.length,
      totalStackAmount: Math.round(totalStackAmount * 100) / 100,
      highestTier,
      anyFast: highestTier === "fast",
      anyFanBoost: sorted.some((o) => !!o.fanBoost),
      comments,
      contributors: contributors.slice().reverse(),
    });
  }

  stacks.sort((a, b) => {
    const ta = a.lead.createdAt ? new Date(a.lead.createdAt).getTime() : 0;
    const tb = b.lead.createdAt ? new Date(b.lead.createdAt).getTime() : 0;
    return tb - ta;
  });

  return stacks;
}
